// src/pages/about/changelog/LocalStorageInfo.jsx
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Stack,
    Button,
    List,
    ListItem,
    ListItemText,
    Divider,
    Paper,
    Snackbar,
    Alert,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StorageIcon from '@mui/icons-material/Storage';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';

// Fungsi utilitas internal
const loadLocalStorageInfo = () => {
    try {
        const allKeys = Object.keys(localStorage);
        const items = [];
        let totalSize = 0;
        let userCacheCount = 0;

        // Analisis semua item di localStorage
        allKeys.forEach(key => {
            try {
                const value = localStorage.getItem(key);
                const itemSize = new Blob([value]).size;
                totalSize += itemSize;
                
                // Coba parse untuk menentukan jenis data
                let parsedValue = value;
                try {
                    parsedValue = JSON.parse(value);
                    
                    // Deteksi apakah ini user data berdasarkan struktur
                    const isUserData = (
                        // Struktur data Anda
                        (parsedValue && parsedValue.data && parsedValue.data.uid) ||
                        // Atau struktur langsung
                        (parsedValue && parsedValue.uid && parsedValue.email) ||
                        // Atau key mengandung 'user' atau 'cache'
                        key.toLowerCase().includes('user') ||
                        key.toLowerCase().includes('cache') ||
                        key.toLowerCase().includes('data')
                    );
                    
                    if (isUserData) {
                        userCacheCount++;
                        
                        items.push({
                            key,
                            size: itemSize,
                            value: parsedValue,
                            isUserData: true,
                            structure: 'direct'
                        });
                    } else {
                        items.push({
                            key,
                            size: itemSize,
                            value: parsedValue,
                            isUserData: false
                        });
                    }
                    
                } catch (e) {
                    // Jika tidak bisa parse, tampilkan sebagai string
                    const isUserData = key.toLowerCase().includes('user') || key.toLowerCase().includes('cache');
                    items.push({
                        key,
                        size: itemSize,
                        value: value,
                        isUserData: isUserData,
                        isJSON: false
                    });
                }
            } catch (e) {
                console.error(`Error processing localStorage key ${key}:`, e);
            }
        });

        return {
            totalItems: allKeys.length,
            userCacheItems: userCacheCount,
            totalSize: totalSize,
            items: items.sort((a, b) => {
                // Urutkan: user data dulu, kemudian berdasarkan key
                if (a.isUserData && !b.isUserData) return -1;
                if (!a.isUserData && b.isUserData) return 1;
                return a.key.localeCompare(b.key);
            })
        };
    } catch (error) {
        console.error("Error loading localStorage info:", error);
        throw error;
    }
};

const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const formatLocalStorageItem = (item) => {
    if (item.isUserData && typeof item.value === 'object' && item.value !== null) {
        // Handle struktur nested (data: {...})
        const userData = item.value.data || item.value;
        
        return (
            <Box sx={{ fontSize: '0.875rem' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {item.key} {item.isJSON === false && '(Non-JSON)'}
                </Typography>
                <List dense sx={{ pl: 2 }}>
                    {userData.uid && (
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText 
                                primary="UID" 
                                secondary={userData.uid} 
                                secondaryTypographyProps={{ sx: { fontFamily: 'monospace' } }}
                            />
                        </ListItem>
                    )}
                    {userData.name && (
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText 
                                primary="Name" 
                                secondary={userData.name} 
                            />
                        </ListItem>
                    )}
                    {userData.email && (
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText 
                                primary="Email" 
                                secondary={userData.email} 
                            />
                        </ListItem>
                    )}
                    {userData.role && (
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText 
                                primary="Role" 
                                secondary={userData.role} 
                            />
                        </ListItem>
                    )}
                    {userData.avatar && (
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText 
                                primary="Avatar" 
                                secondary={
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            wordBreak: 'break-all',
                                            fontSize: '0.75rem',
                                            color: 'text.secondary'
                                        }}
                                    >
                                        {userData.avatar.length > 100 
                                            ? `${userData.avatar.substring(0, 100)}...` 
                                            : userData.avatar}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    )}
                    {userData.timestamp && (
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText 
                                primary="Timestamp" 
                                secondary={
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {new Date(userData.timestamp).toLocaleString()} 
                                        ({userData.timestamp})
                                    </Typography>
                                }
                            />
                        </ListItem>
                    )}
                    {/* Tampilkan semua properti lainnya */}
                    {Object.entries(userData).map(([key, value]) => {
                        if (['uid', 'name', 'email', 'role', 'avatar', 'timestamp'].includes(key)) {
                            return null;
                        }
                        return (
                            <ListItem key={key} sx={{ py: 0.5 }}>
                                <ListItemText 
                                    primary={key} 
                                    secondary={
                                        typeof value === 'string' 
                                            ? value 
                                            : JSON.stringify(value)
                                    }
                                    secondaryTypographyProps={{ 
                                        sx: { 
                                            fontSize: '0.75rem',
                                            fontFamily: 'monospace'
                                        }
                                    }}
                                />
                            </ListItem>
                        );
                    })}
                </List>
            </Box>
        );
    } else {
        return (
            <Box sx={{ fontSize: '0.875rem' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Key: {item.key}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ 
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    wordBreak: 'break-all'
                }}>
                    {typeof item.value === 'object' 
                        ? JSON.stringify(item.value, null, 2) 
                        : String(item.value)}
                </Typography>
            </Box>
        );
    }
};

const LocalStorageInfo = () => {
    // State internal untuk komponen ini saja
    const [localStorageInfo, setLocalStorageInfo] = useState({
        totalItems: 0,
        userCacheItems: 0,
        totalSize: 0,
        items: []
    });
    const [showLocalStorageInfo, setShowLocalStorageInfo] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    const handleToggleLocalStorageInfo = () => {
        if (!showLocalStorageInfo) {
            try {
                const info = loadLocalStorageInfo();
                setLocalStorageInfo(info);
            } catch (error) {
                setSnackbar({
                    open: true,
                    message: "Gagal memuat informasi localStorage: " + error.message,
                    severity: "warning",
                });
            }
        }
        setShowLocalStorageInfo(!showLocalStorageInfo);
    };

    const handleRefreshInfo = () => {
        try {
            const info = loadLocalStorageInfo();
            setLocalStorageInfo(info);
            setSnackbar({
                open: true,
                message: "LocalStorage info diperbarui",
                severity: "success",
            });
        } catch (error) {
            setSnackbar({
                open: true,
                message: "Gagal memuat informasi localStorage: " + error.message,
                severity: "warning",
            });
        }
    };

    const handleClearUserCache = () => {
        if (window.confirm("Apakah Anda yakin ingin menghapus semua data user dari localStorage?")) {
            try {
                const keysToRemove = [];
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    const value = localStorage.getItem(key);
                    
                    try {
                        const parsed = JSON.parse(value);
                        // Cek apakah ini data user berdasarkan struktur
                        const isUserData = (
                            (parsed && parsed.data && parsed.data.uid) ||
                            (parsed && parsed.uid && parsed.email) ||
                            key.toLowerCase().includes('user') ||
                            key.toLowerCase().includes('cache') ||
                            key.toLowerCase().includes('data')
                        );
                        
                        if (isUserData) {
                            keysToRemove.push(key);
                        }
                    } catch (e) {
                        // Jika bukan JSON, cek berdasarkan key name
                        if (key.toLowerCase().includes('user') || key.toLowerCase().includes('cache')) {
                            keysToRemove.push(key);
                        }
                    }
                }
                
                keysToRemove.forEach(key => {
                    console.log(`Removing: ${key}`);
                    localStorage.removeItem(key);
                });
                
                // Refresh info setelah menghapus
                const info = loadLocalStorageInfo();
                setLocalStorageInfo(info);
                
                setSnackbar({
                    open: true,
                    message: `Berhasil menghapus ${keysToRemove.length} data user dari localStorage`,
                    severity: "success",
                });
            } catch (error) {
                console.error("Error clearing user cache:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal menghapus data user: " + error.message,
                    severity: "error",
                });
            }
        }
    };

    const handleViewAllLocalStorage = () => {
        try {
            const allData = {};
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                const value = localStorage.getItem(key);
                try {
                    allData[key] = JSON.parse(value);
                } catch (e) {
                    allData[key] = value;
                }
            }
            
            console.log("All localStorage data:", allData);
            
            setSnackbar({
                open: true,
                message: `Ditemukan ${localStorage.length} item di localStorage. Lihat di console.`,
                severity: "info",
            });
        } catch (error) {
            console.error("Error viewing all localStorage:", error);
            setSnackbar({
                open: true,
                message: "Gagal melihat semua localStorage: " + error.message,
                severity: "error",
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <>
            <Accordion 
                expanded={showLocalStorageInfo}
                onChange={handleToggleLocalStorageInfo}
                sx={{ 
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    border: '1px solid #e0e0e0',
                    borderRadius: 1
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        backgroundColor: showLocalStorageInfo ? 'action.selected' : 'transparent',
                        borderBottom: showLocalStorageInfo ? '1px solid #e0e0e0' : 'none',
                        minHeight: 48,
                        '& .MuiAccordionSummary-content': {
                            alignItems: 'center',
                            gap: 1
                        }
                    }}
                >
                    <StorageIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="medium">
                        LocalStorage Information
                    </Typography>
                    <Chip 
                        label={`${localStorageInfo.userCacheItems} user cache`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ ml: 2 }}
                    />
                    <Chip 
                        label={formatBytes(localStorageInfo.totalSize)}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ ml: 1 }}
                    />
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 2 }}>
                    <Stack spacing={2}>
                        {/* Summary Stats */}
                        <Box sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 2,
                            p: 2,
                            backgroundColor: 'background.default',
                            borderRadius: 1
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <InfoIcon color="info" fontSize="small" />
                                <Typography variant="body2">
                                    Total Items: <strong>{localStorageInfo.totalItems}</strong>
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <StorageIcon color="primary" fontSize="small" />
                                <Typography variant="body2">
                                    User Cache: <strong>{localStorageInfo.userCacheItems}</strong>
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WarningIcon color="warning" fontSize="small" />
                                <Typography variant="body2">
                                    Total Size: <strong>{formatBytes(localStorageInfo.totalSize)}</strong>
                                </Typography>
                            </Box>
                        </Box>

                        {/* Action Buttons */}
                        <Stack direction="row" spacing={1}>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={handleRefreshInfo}
                            >
                                Refresh Info
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={handleViewAllLocalStorage}
                            >
                                View All
                            </Button>
                            {localStorageInfo.userCacheItems > 0 && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={handleClearUserCache}
                                >
                                    Clear User Data
                                </Button>
                            )}
                        </Stack>

                        {/* User Cache Items List */}
                        {localStorageInfo.items.length > 0 ? (
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                    User Cache Items ({localStorageInfo.items.length}):
                                </Typography>
                                <List dense sx={{ 
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    maxHeight: 300,
                                    overflow: 'auto'
                                }}>
                                    {localStorageInfo.items.map((item, index) => (
                                        <React.Fragment key={item.key}>
                                            <ListItem alignItems="flex-start" sx={{ py: 1 }}>
                                                <ListItemText
                                                    primary={
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Chip 
                                                                label={formatBytes(item.size)}
                                                                size="small"
                                                                color="default"
                                                                variant="outlined"
                                                            />
                                                            <Typography variant="body2" fontWeight="medium">
                                                                {item.key}
                                                            </Typography>
                                                        </Stack>
                                                    }
                                                    secondary={formatLocalStorageItem(item)}
                                                />
                                            </ListItem>
                                            {index < localStorageInfo.items.length - 1 && (
                                                <Divider component="li" />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </List>
                            </Box>
                        ) : (
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                alignItems: 'center',
                                p: 3,
                                border: '1px dashed #e0e0e0',
                                borderRadius: 1
                            }}>
                                <Typography variant="body2" color="text.secondary">
                                    Tidak ada user cache ditemukan di localStorage
                                </Typography>
                            </Box>
                        )}

                        {/* Technical Info */}
                        <Box sx={{ 
                            p: 2, 
                            backgroundColor: 'grey.50',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.200'
                        }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                <strong>Note:</strong> User cache digunakan untuk menyimpan data pengguna sementara 
                                untuk meningkatkan performa aplikasi. Data ini diambil dari Firebase dan disimpan 
                                di localStorage browser Anda. Cache akan otomatis diperbarui saat Anda login ulang.
                            </Typography>
                        </Box>
                    </Stack>
                </AccordionDetails>
            </Accordion>

            {/* Snackbar internal untuk komponen ini */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default LocalStorageInfo;